import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, Loader2, BookOpen, Share2 } from 'lucide-react';
import { shareOrCopy, buildBlogShareUrl } from '../lib/share';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true); setNotFound(false);
      try {
        const { data } = await axios.get(`${API}/blog/${slug}`);
        setPost(data);
        // Per-page <title> + meta for SEO when crawlers execute JS (most modern bots do).
        if (data?.title) document.title = `${data.title} — KoLV`;
        const desc = data?.meta_description || '';
        const heroAbs = data?.hero_image_url && data.hero_image_url.startsWith('/')
          ? `${window.location.origin}${data.hero_image_url}`
          : (data?.hero_image_url || `${window.location.origin}/og-banner.png`);
        const canonical = `${window.location.origin}/blog/${data?.slug || slug}`;

        const ensureMeta = (sel, attrs) => {
          let el = document.head.querySelector(sel);
          if (!el) {
            el = document.createElement('meta');
            Object.entries(attrs.create || {}).forEach(([k, v]) => el.setAttribute(k, v));
            document.head.appendChild(el);
          }
          Object.entries(attrs.set || {}).forEach(([k, v]) => el.setAttribute(k, v));
        };
        const metaDescTag = document.querySelector('meta[name="description"]');
        if (metaDescTag && desc) metaDescTag.setAttribute('content', desc);
        ensureMeta('meta[property="og:title"]', { set: { content: data.title } });
        ensureMeta('meta[property="og:description"]', { set: { content: desc || data.title } });
        ensureMeta('meta[property="og:type"]', { set: { content: 'article' } });
        ensureMeta('meta[property="og:url"]', { create: { property: 'og:url' }, set: { content: canonical } });
        ensureMeta('meta[property="og:image"]', { create: { property: 'og:image' }, set: { content: heroAbs } });
        ensureMeta('meta[name="twitter:title"]', { set: { content: data.title } });
        ensureMeta('meta[name="twitter:description"]', { set: { content: desc || data.title } });
        ensureMeta('meta[name="twitter:image"]', { create: { name: 'twitter:image' }, set: { content: heroAbs } });

        // <link rel="canonical">
        let canon = document.head.querySelector('link[rel="canonical"]');
        if (!canon) {
          canon = document.createElement('link');
          canon.setAttribute('rel', 'canonical');
          document.head.appendChild(canon);
        }
        canon.setAttribute('href', canonical);

        // JSON-LD Article structured data — helps Google rich snippets.
        const ld = {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": data.title,
          "description": desc,
          "image": heroAbs,
          "datePublished": data.created_at,
          "dateModified": data.updated_at || data.created_at,
          "author": { "@type": "Organization", "name": "KoLV" },
          "publisher": {
            "@type": "Organization",
            "name": "KoLV",
            "logo": { "@type": "ImageObject", "url": `${window.location.origin}/favicon.svg` }
          },
          "mainEntityOfPage": canonical,
        };
        let ldTag = document.getElementById('kolv-blog-jsonld');
        if (!ldTag) {
          ldTag = document.createElement('script');
          ldTag.id = 'kolv-blog-jsonld';
          ldTag.type = 'application/ld+json';
          document.head.appendChild(ldTag);
        }
        ldTag.textContent = JSON.stringify(ld);
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
        else console.error(e);
      } finally { setLoading(false); }
    })();
    return () => {
      document.title = 'KoLV - Ko Latvietis Vēlas';
      // Restore default og:image / og:type
      const ogImg = document.querySelector('meta[property="og:image"]');
      if (ogImg) ogImg.setAttribute('content', `${window.location.origin}/og-banner.png`);
      const ogType = document.querySelector('meta[property="og:type"]');
      if (ogType) ogType.setAttribute('content', 'website');
      const ld = document.getElementById('kolv-blog-jsonld');
      if (ld) ld.remove();
    };
  }, [slug]);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(
      language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-US',
      { year: 'numeric', month: 'long', day: 'numeric' }
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#D97736] animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
        <div className="text-center">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-[#D97736]/40" />
          <h1 className="font-outfit text-2xl font-bold text-[#121212] mb-2">
            {language === 'lv' ? 'Raksts nav atrasts' : language === 'ru' ? 'Статья не найдена' : 'Post not found'}
          </h1>
          <Button onClick={() => navigate('/blog')} className="mt-4 bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="blog-back-to-index">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'lv' ? 'Atpakaļ uz blogu' : language === 'ru' ? 'Назад в блог' : 'Back to blog'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="blog-post-page">
        {/* Hero */}
        {post.hero_image_url && (
          <div className="w-full aspect-[16/6] max-h-[420px] overflow-hidden bg-[#F3F2EE]">
            <img
              src={post.hero_image_url}
              alt={post.title}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-[#5C5C5C] hover:text-[#D97736] mb-6">
            <ArrowLeft className="w-4 h-4" />
            {language === 'lv' ? 'Visi raksti' : language === 'ru' ? 'Все статьи' : 'All articles'}
          </Link>

          <h1 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-[#121212] mb-4 leading-tight">
            {post.title}
          </h1>

          <div className="flex items-center justify-between flex-wrap gap-3 mb-8 pb-6 border-b border-[#E5E4E0]">
            <div className="flex items-center gap-3 text-sm text-[#5C5C5C]">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
              {post.tags?.length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                  {post.tags.map((t) => (
                    <span key={t} className="px-2 py-0.5 text-xs rounded-full bg-[#F3F2EE] text-[#5C5C5C]">#{t}</span>
                  ))}
                </div>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                // Share the OG endpoint URL so social-media crawlers render a
                // rich preview (title + hero image + description) instead of
                // the bare React HTML.
                const shareUrl = buildBlogShareUrl(post.slug || slug);
                const res = await shareOrCopy({ url: shareUrl, title: post.title });
                if (res.ok && res.mode === 'clipboard') {
                  toast.success(language === 'lv' ? 'Saite nokopēta' : language === 'ru' ? 'Ссылка скопирована' : 'Link copied');
                }
              }}
              data-testid="blog-share-btn"
            >
              <Share2 className="w-4 h-4 mr-1" />
              {language === 'lv' ? 'Dalīties' : language === 'ru' ? 'Поделиться' : 'Share'}
            </Button>
          </div>

          {/* Body */}
          {/*
            Content comes from an external SEO-content service we trust (Bearer-auth gated).
            React requires dangerouslySetInnerHTML for HTML payloads — the trade-off is acceptable
            given the upstream is authenticated and not user-generated.
          */}
          <div
            className="prose prose-lg max-w-none prose-headings:font-outfit prose-headings:text-[#121212] prose-a:text-[#D97736] prose-img:rounded-2xl"
            dangerouslySetInnerHTML={{ __html: post.content_html || '' }}
            data-testid="blog-post-body"
          />

          {/* Footer CTA */}
          <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-[#D97736]/10 via-[#F0A05A]/5 to-transparent border border-[#D97736]/30">
            <h3 className="font-outfit font-semibold text-lg text-[#121212] mb-2">
              {language === 'lv' ? 'Atklāj vairāk KoLV.lv' : language === 'ru' ? 'Узнайте больше на KoLV.lv' : 'Discover more on KoLV.lv'}
            </h3>
            <p className="text-sm text-[#5C5C5C] mb-4">
              {language === 'lv'
                ? 'Atrodi vietējos ražotājus, pakalpojumus un pasākumus visā Latvijā.'
                : language === 'ru'
                ? 'Найдите местных производителей, услуги и мероприятия по всей Латвии.'
                : 'Find local producers, services and events across Latvia.'}
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => navigate('/listings')} className="bg-[#D97736] hover:bg-[#C06528] text-white">
                {language === 'lv' ? 'Pārlūkot sludinājumus' : language === 'ru' ? 'Смотреть объявления' : 'Browse listings'}
              </Button>
              <Button variant="outline" onClick={() => navigate('/catalog')}>
                {language === 'lv' ? 'Atvērt katalogu' : language === 'ru' ? 'Открыть каталог' : 'Open catalog'}
              </Button>
            </div>
          </div>
        </article>
      </div>
  );
}
